import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../../shared/directives/has-permission.directive';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketsPipelineDrawerService } from '../../../services/tickets-config-drawer.services';
import { TicketPipeline } from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-pipelines-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent, HasPermissionDirective],
  templateUrl: './pipelines-list.component.html'
})
export class TicketsPipelinesListComponent implements OnInit, OnDestroy {
  items: TicketPipeline[] = [];
  filtered: TicketPipeline[] = [];
  loading = false;
  error = '';

  filtroNombre = '';
  filtroSoloActivos = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    protected router: Router,
    private configApi: TicketsConfigApiService,
    private drawer: TicketsPipelineDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargar();
    this.drawer.entitySaved$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.configApi.getPipelines().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.items = data.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        this.aplicarFiltros();
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar pipelines';
        this.loading = false;
      }
    });
  }

  aplicarFiltros(): void {
    const term = this.filtroNombre.trim().toLowerCase();
    this.filtered = this.items.filter((p) => {
      if (term && !p.name.toLowerCase().includes(term)) return false;
      if (this.filtroSoloActivos && !p.isActive) return false;
      return true;
    });
  }

  nuevo(): void { this.drawer.open(); }
  editar(item: TicketPipeline): void { this.drawer.open({ id: item.id }); }
  verEtapas(item: TicketPipeline): void {
    this.router.navigate(['/tickets/config/pipelines', item.id]);
  }

  toggleActivo(item: TicketPipeline): void {
    this.configApi.updatePipeline(item.id, {
      name: item.name,
      description: item.description ?? undefined,
      isDefault: item.isDefault,
      isActive: !item.isActive,
      sortOrder: item.sortOrder
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.cargar(),
      error: (err) => this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo actualizar el pipeline' })
    });
  }

  async eliminar(item: TicketPipeline): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar pipeline',
      message: `¿Eliminar el pipeline "${item.name}"?`
    });
    if (!ok) return;
    this.configApi.deletePipeline(item.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => { this.toast.deleted('Pipeline', `Se eliminó "${item.name}" correctamente.`); this.cargar(); },
      error: (err) => this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo eliminar el pipeline' })
    });
  }

  volverATickets(): void { this.router.navigate(['/tickets']); }
}
